<template>
  <div class="dropdown-container" ref="dropdownContainer">
    <button
      class="language-button"
      @click="toggleDropdown"
      :aria-expanded="dropdownOpen.toString()"
      aria-haspopup="listbox"
    >
      <span
        v-if="currentLanguage.flag"
        class="language-flag"
        :style="{ backgroundImage: 'url(' + currentLanguage.flag + ')' }"
        aria-hidden="true"
      ></span>
      <span class="language-label">{{ currentLanguage.label }}</span>
      <span class="arrow" :class="{ open: dropdownOpen }">
        <svg viewBox="0 0 20 12" aria-hidden="true">
          <polyline points="2,2 10,10 18,2" />
        </svg>
      </span>
    </button>

    <transition name="dropdown-fade">
      <div v-if="dropdownOpen" class="dropdown-menu" role="listbox">
        <div
          v-for="lang in dropdownLanguages"
          :key="lang.code"
          class="dropdown-item"
          :class="{ active: lang.code === currentLanguage.code }"
          role="option"
          :aria-selected="lang.code === currentLanguage.code"
          @click="setLanguage(lang)"
        >
          <span
            v-if="lang.flag"
            class="dropdown-flag"
            :style="{ backgroundImage: 'url(' + lang.flag + ')' }"
            aria-hidden="true"
          ></span>
          <span class="dropdown-label">{{ lang.label }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "CustomLanguageDropdown",

  data() {
    return {
      dropdownOpen: false,

      // Mockitud keeled (kolm kastikest tulevikus backendist)
      fallbackFlags: {
        no: "https://flagcdn.com/no.svg",
        en: "https://flagcdn.com/gb.svg",
        sv: "https://flagcdn.com/se.svg",
      },
      fallbackLanguages: [
        { code: "no", label: "Norsk" },
        { code: "en", label: "English" },
        { code: "sv", label: "Svenska" },
      ],

      currentLanguageCode: "en",
    };
  },

  computed: {
    ...mapState({
      storeLanguages: (state) =>
        (state.wf && Array.isArray(state.wf.languages)
          ? state.wf.languages
          : []) || [],
      storeLanguageCode: (state) =>
        state.wf && state.wf.language ? state.wf.language : null,
    }),

    dropdownLanguages() {
      const fromStore =
        this.storeLanguages && this.storeLanguages.length
          ? this.storeLanguages
              .map((lang) => this.mapStoreLanguage(lang))
              .filter((lang) => !!lang)
          : [];

      const merged = new Map();

      this.fallbackLanguages.forEach((fallback) => {
        merged.set(fallback.code, {
          ...fallback,
          flag:
            (fallback.flag && this.getFlagUrl(fallback.flag)) ||
            this.getFallbackFlag(fallback.code),
        });
      });

      fromStore.forEach((lang) => {
        merged.set(lang.code, {
          ...merged.get(lang.code),
          ...lang,
          flag: lang.flag || this.getFallbackFlag(lang.code),
        });
      });

      return Array.from(merged.values());
    },

    currentLanguage() {
      const code = (this.currentLanguageCode || "").toLowerCase();
      return (
        this.dropdownLanguages.find((lang) => lang.code === code) ||
        this.dropdownLanguages[0] || {
          code: "",
          label: "",
          flag: null,
        }
      );
    },
  },

  mounted() {
    const saved = localStorage.getItem("selectedLanguage");
    if (saved) {
      this.currentLanguageCode = saved.toLowerCase();
    } else if (this.storeLanguageCode) {
      this.currentLanguageCode = this.storeLanguageCode.toLowerCase();
    }
    this.ensureLanguageSelected();

    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);
  },

  watch: {
    storeLanguageCode: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const normalized = newVal.toLowerCase();
          if (this.currentLanguageCode !== normalized) {
            this.currentLanguageCode = normalized;
            localStorage.setItem("selectedLanguage", normalized);
          }
        }
      },
    },
    dropdownLanguages(newList, oldList) {
      if (!oldList.length && newList.length) {
        this.ensureLanguageSelected();
      }
    },
  },

  methods: {
    handleClickOutside(event) {
      // Close dropdown if click is outside the dropdown container
      if (this.dropdownOpen && this.$refs.dropdownContainer && !this.$refs.dropdownContainer.contains(event.target)) {
        this.dropdownOpen = false;
      }
    },

    ensureLanguageSelected() {
      if (!this.dropdownLanguages.length) {
        return;
      }
      const target =
        this.dropdownLanguages.find(
          (lang) => lang.code === this.currentLanguageCode
        ) || this.dropdownLanguages[0];

      if (target) {
        this.setLanguage(target, { silent: true });
      }
    },

    mapStoreLanguage(lang) {
      const rawCode =
        (typeof lang.getName === "function" && lang.getName()) ||
        lang.name ||
        lang.code;

      if (!rawCode) {
        return null;
      }

      const code = rawCode.toLowerCase();
      const label =
        (typeof lang.getNativeName === "function" && lang.getNativeName()) ||
        lang.label ||
        lang.name ||
        rawCode.toUpperCase();

      return {
        code,
        label,
        flag:
          this.getFlagUrl(
            (typeof lang.getFlagImage === "function" && lang.getFlagImage()) ||
              lang.flagImage
          ) || this.getFallbackFlag(code),
        original: lang,
      };
    },

    getFallbackFlag(code) {
      return this.fallbackFlags[code] || null;
    },

    getFlagUrl(flagId) {
      if (!flagId) {
        return null;
      }

      try {
        const api =
          (typeof window !== "undefined" && window.WayfinderAPI) || undefined;

        if (api && typeof api.getURL === "function") {
          return api.getURL("images", "thumbnail", [flagId]);
        }
      } catch (error) {
        console.warn("Failed to resolve Wayfinder flag image", error);
      }

      return null;
    },

    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },

    setLanguage(lang, options = {}) {
      if (!lang) {
        return;
      }

      const codeLower = (lang.code || "").toLowerCase();
      this.currentLanguageCode = codeLower;
      this.dropdownOpen = false;
      localStorage.setItem("selectedLanguage", codeLower);

      if (options.silent) {
        return;
      }

      this.callWayfinderSetLanguage(codeLower);
    },

    callWayfinderSetLanguage(codeLower) {
      // proovime mitut tunnetusmärki — store'ist, Vue-prototüübist ja window'ist
      const wf = this.$store && this.$store.wf ? this.$store.wf : null;

      // 2) Vue prototype fallback
      const protoWf = this.$wf || this.$wayfinder || this.$wayfinderApp || null;

      // 3) window fallback
      const winWf = window.wayfinder || window.Wayfinder || null;

      const wfInstance = wf || protoWf || winWf;

      if (wfInstance && typeof wfInstance.setLanguage === "function") {
        // paljudel integratsioonidel setLanguage ootab väikeseid keele koode 'en' jne
        try {
          wfInstance.setLanguage(codeLower);
          console.log("Called wf.setLanguage(", codeLower, ")");
        } catch (e) {
          console.warn("wf.setLanguage failed:", e);
        }
      } else {
        console.warn("Wayfinder instance not found or setLanguage not available", {
          wf,
          protoWf,
          winWf,
        });
      }
    },
  },
};
</script>

<style scoped>
.dropdown-container {
  position: relative;
  display: inline-block;
}

.language-button {
  display: flex;
  align-items: center;
  gap: 16px; 
  background: rgba(100, 98, 98, 0.15);
  border: 1px solid rgba(95, 94, 94, 0.4);
  color: #fff;
  padding: 16px 32px; 
  border-radius: 24px; 
  font-size: 2rem; 
  cursor: pointer;
  backdrop-filter: blur(8px);
  transition: background 0.25s ease, border-color 0.25s ease,
    box-shadow 0.25s ease, transform 0.25s ease;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
  margin-left: 4rem;
}

.language-button:hover {
  background: rgba(100, 98, 98, 0.15);
  border-color: rgba(95, 94, 94, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
}

.language-flag {
  width: 36px; 
  height: 26px; 
  border-radius: 8px; 
  background-size: cover;
  background-position: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.dropdown-menu {
  position: absolute;
  top: 110%;
  left: 0;
  min-width: 180px; 
  background: rgba(9, 24, 33, 0.92);
  border-radius: 18px; 
  overflow: hidden;
  box-shadow: 0 24px 40px rgba(0, 15, 25, 0.35);
  z-index: 2000;
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  transform-origin: top center;
  margin-left: 4rem;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

.arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px; 
  height: 32px; 
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  transition: transform 0.3s ease, background 0.3s ease;
}

.arrow.open {
  transform: rotate(180deg);
}

.arrow svg {
  width: 14px; 
  height: 14px; 
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.language-label {
  letter-spacing: 0.02em;
}

.dropdown-item {
  padding: 16px 22px; 
  cursor: pointer;
  font-size: 2rem; 
  color: rgba(255, 255, 255, 0.86);
  transition: background 0.2s ease, color 0.2s ease
}

.dropdown-item:hover {
  background: rgba(0, 200, 180, 0.16);
  color: #e6fffb;
}

.dropdown-item.active {
  background: rgba(0, 200, 180, 0.28);
  color: #ffffff;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 14px; 
}

.dropdown-flag {
  width: 34px; 
  height: 22px; 
  border-radius: 6px; 
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.dropdown-label {
  flex: 1;
}

.dropdown-item.active .dropdown-flag {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6);
}


@media (max-width: 480px) {
  .language-button {
    padding: 8px 18px;
    font-size: 1.8rem;
    border-radius: 16px;
  }
  
  .language-flag {
    width: 28px;
    height: 20px;
  }
  
  .arrow {
    width: 26px;
    height: 26px;
  }
  
  .dropdown-menu {
    min-width: 140px;
  }
  
  .dropdown-item {
    padding: 14px 18px;
    font-size: 2.2rem;
  }
  
  .dropdown-flag {
    width: 26px;
    height: 20px;
  }
}
</style>